import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@frontierag/frontier-ui-library';
import {
  CancelOutlined as CancelOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
} from '@frontierag/frontier-ui-library/icons';
import { useField } from 'formik';

import useStyles from './styles';
import type { ISingleSelectField } from './types';

export default function SingleSelectField({
  name,
  options,
  label,
  placeholder = '',
  loading,
  onChange,
  onCloseIconClick,
  autoWidth = true,
  noOptionsFoundText,
  showClearIcon = false,
  required,
  disabled,
  ...props
}: ISingleSelectField) {
  const [{ value }, meta, { setValue, setTouched }] = useField(name);
  const styles = useStyles(showClearIcon);
  const selectRef = useRef<HTMLSelectElement>();
  const { t } = useTranslation();

  const placeholderComponent = useMemo(
    () => (
      <Box sx={styles.placeholder} component="span">
        {placeholder}
      </Box>
    ),
    [styles.placeholder, placeholder],
  );

  const getSelectedOption = useCallback(
    (selected) => {
      if (selected) return options.find((option) => option.value === selected);

      return null;
    },
    [options],
  );

  const closeIconClick = useCallback(async () => {
    const prevValue = value;
    const newVal = '';
    await setValue(newVal);
    if (onCloseIconClick) onCloseIconClick(newVal, prevValue);
  }, [value, onCloseIconClick, setValue]);

  const renderValue = useCallback(
    (selected) => {
      const selectedOption = getSelectedOption(selected);

      return (
        <Box display="flex">
          <Box width="100%">
            {selectedOption ? (
              <Typography noWrap>{selectedOption.label}</Typography>
            ) : (
              placeholderComponent
            )}
          </Box>
          {loading ? <CircularProgress size={20} /> : ''}
        </Box>
      );
    },
    [getSelectedOption, placeholderComponent, loading],
  );

  const handleOnChange = useCallback(
    async (event) => {
      const prevValue = value;

      await setValue(event.target.value);
      if (onChange) onChange(event.target.value, prevValue);
    },
    [value, setValue, onChange],
  );

  const handleOnBlur = useCallback(async () => {
    if (!meta.touched) await setTouched(true, false);
  }, [setTouched, meta.touched]);

  const hasError = Boolean(meta.touched && meta.error && !disabled);
  const helperText = hasError ? meta.error : '';

  const menuMaxWidth =
    !autoWidth && selectRef && selectRef.current && selectRef.current.offsetWidth
      ? selectRef.current.offsetWidth
      : '100%';

  return (
    <FormControl fullWidth required={required}>
      <InputLabel
        id={`single-select-field-label-${name}`}
        error={hasError}
        sx={styles.label}
        shrink
      >
        {label}
      </InputLabel>
      <Select
        name={name}
        data-testid={`single-select-field-${name}`}
        onChange={handleOnChange}
        error={hasError}
        label={label}
        displayEmpty
        renderValue={renderValue}
        value={options && options.length && value ? value : ''}
        onClose={handleOnBlur}
        inputProps={{ placeholder }}
        sx={styles.select}
        MenuProps={{ sx: { ' .MuiPaper-root': { maxWidth: menuMaxWidth } } }}
        ref={selectRef}
        IconComponent={ExpandMoreIcon}
        endAdornment={
          showClearIcon && value && !disabled ? (
            <InputAdornment position="end" sx={styles.clearIcon}>
              <IconButton
                onClick={closeIconClick}
                color="inherit"
                data-testid="single-select-field-close-icon"
              >
                <CancelOutlinedIcon sx={{ width: 24, height: 24 }} />
              </IconButton>
            </InputAdornment>
          ) : null
        }
        disabled={disabled}
        {...props}
      >
        {options.map((option) => (
          <MenuItem value={option.value} key={option.value} sx={styles.menuItem}>
            {option.label}
          </MenuItem>
        ))}

        {!loading && (!options || !options.length) && (
          <MenuItem disabled sx={styles.menuItem}>
            {noOptionsFoundText || t('label.noOptionsFound')}
          </MenuItem>
        )}
        {loading && (
          <MenuItem disabled sx={styles.menuItem}>
            {t('label.loading')}
          </MenuItem>
        )}
      </Select>
      {helperText && <FormHelperText error={hasError}>{helperText}</FormHelperText>}
    </FormControl>
  );
}
